export class Cookie {
    getOwnProperties() {
        return [...Object.keys(Object.getOwnPropertyDescriptors(Cookie.prototype)), ...Object.getOwnPropertyNames(this)]
    }

    constructor(name: string, c: any, enforceWildcardDomain = false) {
        if ("string" !== typeof name) {
            throw new Error(this._format(this._exceptions.nameType))
        }
        if ("" === name) {
            throw new Error(this._format(this._exceptions.nameEmpty))
        }

        const config = c || {}
        this._cookieName = name

        if (!this.getOwnProperties().length) {
            this._readDocumentCookieData()
        }

        const availableProperties = this.getOwnProperties()
        this._setupPhase = true
        this._prependWildcardDomain = !!enforceWildcardDomain

        for (const option of Object.keys(config)) {
            if (!Object.hasOwn(config, option)) {
                continue
            }

            if (availableProperties.indexOf(option) < 0 || "_" === option.slice(0, 1)) {
                continue
            }

            if (this._prependWildcardDomain && "domain" === option) {
                config[option] = `.${config[option]}`.replace(/^(\.)+/, "$1")
            }

            // @ts-expect-error fix config type yes
            this[option] = config[option]
        }

        this._setupPhase = false

        if (!config.value) {
            return
        }

        this._createCookie()
    }

    /**
     * Possible values for the prefix config property
     *
     * @const
     * @enum {string}
     *
     * @typedef {Object} Cookie.PREFIX
     *  @property { "__Host-" } Host
     *  @property { "__Secure-" } Secure
     */
    static PREFIX = {
        Host: "__Host-",
        Secure: "__Secure-",
    }

    static SAMESITE = {
        Strict: "strict",
        Lax: "lax",
    }

    _setupPhase: boolean
    _cookieName = ""

    _cookieDomain = window.location.hostname

    _prependWildcardDomain = false

    _cookieExpires: null | number | string = null

    _cookieMaxAge: null | number = null

    _cookiePath: string | null = null

    _cookiePrefix: null | string = null

    _cookieSamesite = Cookie.SAMESITE.Lax

    _cookieSecure = true

    _cookieValue: any = ""

    _cookieString = ""

    _cookieData: any = {}

    _unsetExpirationDate = "Thu, 01 Jan 1970 00:00:00 GMT"

    _exceptions = {
        nameType: "The cookie's name must be of type: string.",
        nameEmpty: "The cookie's name cannot be empty.",
        propertyType: "{property} - this property can only be of type: {type}.",
        insecureDomain:
            "{domain} is not a subdomain of {location}. " + "2nd-level domains are not allowed for security reasons.",
        invalidExpiresDate: "`{date}` is not a valid date string.",
        invalidMaxAge: "Property maxAge has to be a number.",
        invalidPrefix: "{prefix} is not a valid prefix.",
        invalidSamesite: "{samesite} is not a valid value for samesite.",
        invalidValue: "Value `{unsupported}` is of unsupported type: {type}.",
    }

    get domain() {
        return this._cookieDomain || location.hostname
    }

    set domain(d: string) {
        let newDomain = d || ""

        if ("" !== newDomain) {
            let url = undefined
            let testDomain = undefined

            if (typeof newDomain !== "string") {
                throw new Error(
                    this._format(this._exceptions.propertyType, {
                        property: "domain",
                        type: "string",
                    }),
                )
            }

            testDomain = newDomain
            if (!testDomain.match(/^https?:\/\//)) {
                testDomain = `https://${testDomain.replace(/^\./, "")}`
            }

            url = this._parseUrlString(testDomain)

            let baseDomain = location.hostname
            let subDomain = url.hostname

            if (url.hostname.length < location.hostname.length) {
                baseDomain = url.hostname
                subDomain = location.hostname
            }

            if (subDomain.indexOf(baseDomain) < 0) {
                throw new Error(
                    this._format(this._exceptions.insecureDomain, {
                        domain: url.hostname,
                        location: location.hostname,
                    }),
                )
            }
        }

        if (this._prependWildcardDomain && newDomain) {
            newDomain = `.${newDomain}`.replace(/^(\.)+/, "$1")
        }

        this.removeCookie()
        this._cookieDomain = newDomain || location.hostname
        this._createCookie()
    }

    get expires() {
        this._checkCookieDataChanged()
        return this._cookieExpires || ""
    }

    /**
     * @public
     * @type {string}
     * @return void
     */
    set expires(newExpires) {
        const checkDate = new Date(newExpires)

        if (Number.isNaN(checkDate.getDate())) {
            throw new Error(
                this._format(this._exceptions.invalidExpiresDate, {
                    date: newExpires,
                }),
            )
        }

        if (this._cookieExpires === newExpires) {
            return
        }

        this.removeCookie()
        this._cookieExpires = newExpires
        this._createCookie()
    }

    /**
     * @public
     * @type {number}
     * @return {number}
     */
    get maxAge() {
        return this._cookieMaxAge || -1
    }

    /**
     * @public
     * @type {number}
     * @return void
     */
    set maxAge(newMaxAge) {
        if (Number.isNaN(Number(newMaxAge))) {
            throw new Error(this._format(this._exceptions.invalidMaxAge, {}))
        }

        if (this._cookieMaxAge === newMaxAge) {
            return
        }

        this.removeCookie()
        this._cookieMaxAge = newMaxAge
        this._createCookie()
    }

    /**
     * @public
     * @type {string}
     * @return {string}
     */
    get path() {
        this._checkCookieDataChanged()
        return this._cookiePath || location.pathname
    }

    /**
     * @public
     * @type {string}
     * @return void
     */
    set path(newPath) {
        this.removeCookie()
        this._cookiePath = newPath || ""
        this._createCookie()
    }

    /**
     * @public
     * @type {string}
     * @return {string}
     */
    get prefix() {
        return this._cookiePrefix || ""
    }

    /**
     * @public
     * @type {string}
     * @return void
     */
    set prefix(newPrefix) {
        if (newPrefix !== Cookie.PREFIX.Host && newPrefix !== Cookie.PREFIX.Secure) {
            throw new Error(
                this._format(this._exceptions.invalidPrefix, {
                    prefix: newPrefix,
                }),
            )
        }

        this.removeCookie()
        this._cookiePrefix = newPrefix
        this._createCookie()
    }

    /**
     * @public
     * @type {string}
     * @return {string}
     */
    get samesite() {
        return this._cookieSamesite || ""
    }

    /**
     * @public
     * @type {string}
     * @return void
     */
    set samesite(newSamesite) {
        if (newSamesite !== Cookie.SAMESITE.Strict && newSamesite !== Cookie.SAMESITE.Lax) {
            throw new Error(
                this._format(this._exceptions.invalidPrefix, {
                    samesite: newSamesite,
                }),
            )
        }

        this.removeCookie()
        this._cookieSamesite = newSamesite
        this._createCookie()
    }

    /**
     * @public
     * @type {boolean}
     * @return {boolean}
     */
    get secure() {
        return this._cookieSecure || false
    }

    /**
     * @public
     * @type {boolean}
     * @return void
     */
    set secure(newSecure) {
        this.removeCookie()
        this._cookieSecure = true === newSecure
        this._createCookie()
    }

    /**
     * @public
     * @type {*}
     * @return {*}
     */
    get value() {
        this._checkCookieDataChanged()
        return this._cookieValue || ""
    }

    /**
     * @public
     * @type {string|number|Object|Array}
     * @return void
     */
    set value(newValue: any) {
        let normalizedValue: string | number = ""

        try {
            switch (typeof newValue) {
                case "boolean":
                    normalizedValue = newValue ? 1 : 0
                    break

                case "number":
                case "bigint":
                case "object":
                case "string":
                    normalizedValue = newValue
                    break

                default:
                    normalizedValue = newValue.toString()
                    break
            }
        } catch (exception) {
            throw new Error(
                this._format(this._exceptions.invalidValue, {
                    unsupported: newValue,
                    type: typeof newValue,
                }),
            )
        }

        this.removeCookie()
        this._cookieValue = normalizedValue
        this._createCookie()
    }

    /**
     * Returns the expires date for cookies that will be set now
     *
     * @public
     */
    static getExpireDate(relativeTimeInSeconds: number) {
        return new Date(Date.now() + relativeTimeInSeconds * 1000).toUTCString()
    }

    /**
     * Short alias for removeCookie
     *
     * @public
     */
    remov() {
        this.removeCookie()
    }

    /**
     * Manually update any Cookie data in case it has been changed without
     * 	using the setter methods
     *
     * @public
     */
    updateCookie() {
        this.removeCookie()
        this._createCookie()
    }

    /**
     * Create or recreate a cookie with the current value and settings
     *
     * @private
     */
    _createCookie() {
        if (this._setupPhase) {
            return
        }

        const cookieData = [`${encodeURIComponent(this._cookieName)}=${this._stringifyValue()}`]

        let cookieProperty: string | undefined = undefined
        for (const cooKey of ["domain", "expires", "maxAge", "path", "samesite", "secure"] as const) {
            if (!this[cooKey]) {
                continue
            }

            cookieProperty = cooKey
            if (cookieProperty === "maxAge") {
                if (typeof this[cooKey] === "number" && this[cooKey] < 0) {
                    continue
                }

                cookieProperty = "max-age"
            }

            cookieData.push(`${cookieProperty}=${this[cooKey]}`)
        }

        document.cookie = cookieData.join(";")
    }

    /**
     * Let an old cookie with the current cookie name and settings expire
     * 	so there are no duplicate cookies with the same name in case
     * 	certain values are changed (like domain or path)
     *
     * @private
     */
    removeCookie() {
        if (this._setupPhase) {
            return
        }

        const pastDate = this._unsetExpirationDate
        const cookieData = [`${encodeURIComponent(this._cookieName)}=`]

        for (const cooKey of this.getOwnProperties()) {
            // @ts-expect-error TODO fix
            const property = this[cooKey]
            // Ignore undesired keys
            if ("_" === cooKey[0] || "value" === cooKey || !property || typeof property === "function") {
                continue
            }
            // Ignore expires, it will be set manually
            if ("expires" === cooKey) {
                continue
            }

            // Ignore maxAge, it will be set manually
            if ("maxAge" === cooKey) {
                continue
            }

            cookieData.push(`${cooKey}=${property}`)
        }

        // Past date and low maxAge to remove cookie
        cookieData.push(`expires=${pastDate}`)
        cookieData.push("max-age=1")

        document.cookie = cookieData.join(";")
    }

    /**
     * Save and parse all cookie data for the current document
     *
     * @static
     * @private
     * @return {void}
     */
    _readDocumentCookieData() {
        this._cookieString = document.cookie

        const cookieParts = this._cookieString.split(/;\s*/g)

        // biome-ignore lint: blablabla
        cookieParts.forEach((cookie, index) => void (cookieParts[index] = cookie.replace(/=/, '":"')))

        const jsonString = `{"${cookieParts.join('","')}"}`.replace(/(?:"([{\[])|([\]}])")/g, "$1$2")

        this._cookieData = JSON.parse(jsonString, function parse(key, value) {
            if ("" === key) {
                return value
            }

            if (Number(value) === value || Number(value).toString() === value) {
                return Number(value)
            }

            let newValue: any = decodeURIComponent(value)
            try {
                newValue = JSON.parse(newValue)
            } catch (e) {}

            if ("object" === typeof newValue) {
                for (const objectKey in newValue) {
                    if (!Object.hasOwn(newValue, objectKey)) {
                        continue
                    }

                    newValue[objectKey] = parse(objectKey, newValue[objectKey])
                }
            }

            return newValue
        })
    }

    /**
     * Use the saved cookie string to compare whether something has
     * 	changed since the last time a cookie value was requested
     *
     * @private
     * @return {void}
     */
    _checkCookieDataChanged() {
        if (this._cookieString !== document.cookie) {
            this._readDocumentCookieData()
        }

        if (!this._cookieData[this._cookieName]) {
            this._cookieData[this._cookieName] = ""
        }

        if (this._cookieValue === this._cookieData[this._cookieName]) {
            return
        }

        this._cookieValue = this._cookieData[this._cookieName]
    }

    /**
     * Replace all occurrences of tokens in a template string with the
     * 	property values in the data object
     *
     * @private
     * @param {string} template
     * @param {Object.<string, string>} [data]
     */
    _format(template: string, data: any = undefined) {
        if (!data || !Object.getOwnPropertyNames(data).length) {
            return template.replace(/{(\w+)}/g, "")
        }

        return template.replace(/{(\w+)}/g, (_, dataKey) => data[dataKey])
    }

    _parseUrlString(urlString: string) {
        return new URL(urlString)
    }

    _stringifyValue() {
        switch (typeof this._cookieValue) {
            case "number":
            case "bigint":
                return this._cookieValue

            case "object":
                return encodeURIComponent(JSON.stringify(this._cookieValue))

            default:
                return encodeURIComponent(this._cookieValue)
        }
    }
}
